import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { mediaListQueryKey, type BulkMediaOp, type HotelMedia } from '../features/media/api.js';
import { MediaLibraryBrowser } from '../features/media/media-library-browser.js';
import { MediaUploadPanel } from '../features/media/media-upload-panel.js';
import { MediaDetailDialog } from '../features/media/media-detail-dialog.js';
import { MediaBulkBar } from '../features/media/media-bulk-bar.js';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';

/**
 * Split out from `Route`'s component so a spec can render the page directly
 * with a `hotelId` prop, without dragging in the TanStack Router context a
 * route match needs.
 */
export function HotelMediaPage({ hotelId }: { hotelId: string }) {
  const qc = useQueryClient();

  // Gallery mode (default): click a card to open its detail dialog — that's
  // where tags/compliance are edited now, replacing the old `prompt()`.
  // Selection mode swaps the same browser into `picker` semantics so cards
  // toggle into `selectedIds` for the bulk bar instead.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailMedia, setDetailMedia] = useState<HotelMedia | null>(null);

  // Bumped after any operation that can change which rows belong in the
  // grid (bulk op, a detail-dialog save, a fresh upload) — passed to
  // `MediaLibraryBrowser` as `reloadToken`, which resets it to page one and
  // refetches. Per the controller ruling this replaces trying to reconcile
  // an already-loaded later page against a partial change: a delete (or a
  // filter-relevant edit) on page one is invisible to a refetch of page two.
  const [reloadToken, setReloadToken] = useState(0);
  function bumpReload() {
    setReloadToken((t) => t + 1);
  }

  function toggleSelectMode() {
    setSelectMode((v) => !v);
    setSelectedIds([]);
  }

  function handleUploaded(uploaded: HotelMedia[]) {
    if (uploaded.length === 0) return;
    void qc.invalidateQueries({ queryKey: mediaListQueryKey(hotelId) });
    bumpReload();
  }

  // The bar itself never clears the selection (it doesn't own that state).
  // Clearing it after every op would unmount the bar mid-message; keeping it
  // after tagging/rights updates lets a colleague keep working the same set.
  // Only a delete drops the selection, since the deleted ids no longer exist
  // to keep selected.
  function handleBulkDone(op: BulkMediaOp) {
    bumpReload();
    if (op === 'delete') setSelectedIds([]);
  }

  return (
    <div className="space-y-6">
      <MediaUploadPanel hotelId={hotelId} onUploaded={handleUploaded} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Médiatár</CardTitle>
            <CardDescription>
              Szűrés címke és tájolás szerint — kattints egy képre a részletekért.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" aria-pressed={selectMode} onClick={toggleSelectMode}>
            {selectMode ? 'Kijelölés befejezése' : 'Kijelölés'}
          </Button>
        </CardHeader>
        <MediaLibraryBrowser
          hotelId={hotelId}
          mode={selectMode ? 'select' : 'gallery'}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onOpenDetail={setDetailMedia}
          reloadToken={reloadToken}
        />
      </Card>

      {detailMedia && (
        <MediaDetailDialog
          hotelId={hotelId}
          media={detailMedia}
          onClose={() => setDetailMedia(null)}
          onSaved={bumpReload}
          onDeleted={bumpReload}
        />
      )}

      {selectMode && (
        <MediaBulkBar hotelId={hotelId} selectedIds={selectedIds} onDone={handleBulkDone} />
      )}
    </div>
  );
}

function HotelMediaRoute() {
  const { hotelId } = Route.useParams();
  return <HotelMediaPage hotelId={hotelId} />;
}

export const Route = createFileRoute('/app/hotels/$hotelId/media')({
  component: HotelMediaRoute,
});
